'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import useSWR, { mutate } from 'swr';
import customFetch, { researchFetch } from '@/lib/utils/fetch';
import { useChatMode } from './ChatModeContext';

interface Owner {
  id: string;
  name: string;
  email: string;
}

interface Workspace {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  owner: Owner;
  emoji: string;
  memberCount: number;
  userRole: 'owner';
  userJoinedAt: string;
}

export interface WorkspaceMembers {
  email: string;
  id: string;
  joinedAt: string;
  name: string;
  role: string;
  userId: string;
}

interface WorkspaceContextType {
  // 所有项目列表
  workspaces: Workspace[];
  // 当前选中的项目详情
  currentWorkspace: Workspace | null;
  // 加载状态
  isLoadingWorkspaces: boolean;
  currentWorkspaceMembers: WorkspaceMembers[] | null;
  isLoadingCurrentWorkspace: boolean;
  // 错误状态
  workspacesError: any;
  currentWorkspaceError: any;
  // 操作方法
  fetchWorkspaces: () => Promise<void>;
  fetchWorkspaceById: (teamId: string) => Promise<void>;
  createWorkspace: (teamData: Partial<Workspace>) => Promise<Workspace>;
  updateWorkspace: (teamId: string, updates: Partial<Workspace>) => Promise<void>;
  deleteWorkspace: (teamId: string) => Promise<void>;
  leaveWorkspace: (teamId: string, userId: string) => Promise<void>;
  updateWorkspaceInstructions: (teamId: string, instructions: string) => Promise<void>;
  // 辅助方法
  getWorkspaceById: (teamId: string) => Workspace | undefined;
  refreshWorkspaces: () => void;
  refreshCurrentWorkspace: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspaces must be used within a WorkspaceProvider');
  }
  return context;
};

interface WorkspaceProviderProps {
  children: ReactNode;
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ children }) => {
  const { data: researchAccessToken } = useSWR('researchAccessToken');
  const { workspaceId } = useChatMode();
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(workspaceId || null);
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);

  // 获取所有 My Workspace 列表
  const {
    data: workspaceData,
    error: workspacesError,
    isLoading: isLoadingWorkspaces,
    mutate: mutateWorkspaces,
  } = useSWR(researchAccessToken ? 'research:/teams' : null, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
  });

  const workspaces = React.useMemo(() => workspaceData?.data || [], [workspaceData]);

  // 获取当前Workspace详情
  const {
    data: currentWorkspaceData,
    error: currentWorkspaceError,
    isLoading: isLoadingCurrentWorkspace,
    mutate: mutateCurrentWorkspace,
  } = useSWR(currentWorkspaceId ? `research:/teams/${currentWorkspaceId}/members` : null, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
  });

  // 获取所有项目
  const fetchWorkspaces = useCallback(async () => {
    await mutateWorkspaces();
  }, [mutateWorkspaces]);

  // 根据ID获取项目详情
  const fetchWorkspaceById = useCallback(
    async (teamId: string) => {
      setCurrentWorkspaceId(teamId);
      if (teamId) {
        await mutateCurrentWorkspace();
      }
    },
    [mutateCurrentWorkspace],
  );

  // 创建新项目
  const createWorkspace = useCallback(
    async (teamData: Partial<Workspace>) => {
      const response = await researchFetch('api/teams', {
        method: 'POST',
        body: JSON.stringify(teamData),
      });

      if (!response?.ok) {
        throw new Error('Failed to create team');
      }

      const res = await response.json();
      if (!res.success) {
        throw new Error(res.message || 'Failed to create team');
      }
      await mutateWorkspaces(); // 刷新项目列表
      return res.data;
    },
    [mutateWorkspaces],
  );

  // 更新项目
  const updateWorkspace = useCallback(
    async (teamId: string, updates: Partial<Workspace>) => {
      const response = await researchFetch(`api/teams/${teamId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });

      if (!response?.ok) {
        throw new Error('Failed to update team');
      }

      const res = await response.json();
      if (!res.success) {
        throw new Error(res.data.message || 'Failed to update workspace');
      }

      // 刷新数据
      await mutateWorkspaces();
      if (currentWorkspaceId === teamId) {
        await mutateCurrentWorkspace();
      }
      return res.data;
    },
    [mutateWorkspaces, mutateCurrentWorkspace, currentWorkspaceId],
  );

  // 删除项目
  const deleteWorkspace = useCallback(
    async (teamId: string) => {
      const response = await researchFetch(`api/teams/${teamId}`, {
        method: 'DELETE',
      });
      customFetch('/api/market-strategy', {
        method: 'DELETE',
        body: JSON.stringify({ teamId: workspaceId }),
      });

      if (!(response as any)?.ok) {
        throw new Error('Failed to delete workspace');
      }

      const res = await response?.json();
      if (!res.success) {
        throw new Error(res.data.message || 'Failed to delete workspace');
      }

      await mutateWorkspaces();
      if (currentWorkspaceId === teamId) {
        setCurrentWorkspace(null);
        setCurrentWorkspaceId(null);
      }
      return res.data;
    },
    [workspaceId, mutateWorkspaces, currentWorkspaceId],
  );

  const leaveWorkspace = useCallback(
    async (teamId: string, userId: string) => {
      const response = await researchFetch(`api/teams/${teamId}/members/${userId}`, {
        method: 'DELETE',
      });

      if (!response?.ok) {
        throw new Error('Failed to leave workspace');
      }

      const res = await response.json();
      if (!res.success) {
        throw new Error(res.data.message || 'Failed to leave workspace');
      }

      await mutateWorkspaces();
      await mutateCurrentWorkspace();
    },
    [mutateWorkspaces, mutateCurrentWorkspace],
  );

  // 更新项目指令
  const updateWorkspaceInstructions = useCallback(
    async (teamId: string, instructions: string) => {
      // await updateWorkspace(teamId, { instructions });
      await updateWorkspace(teamId, {});
    },
    [updateWorkspace],
  );

  // 根据ID获取项目（从缓存）
  const getWorkspaceById = useCallback(
    (teamId: string) => {
      return workspaces.find((p: Workspace) => p.id === teamId);
    },
    [workspaces],
  );

  // 刷新项目列表
  const refreshWorkspaces = useCallback(() => {
    mutateWorkspaces();
  }, [mutateWorkspaces]);

  // 刷新当前项目
  const refreshCurrentWorkspace = useCallback(() => {
    if (currentWorkspaceId) {
      mutateCurrentWorkspace();
    }
  }, [currentWorkspaceId, mutateCurrentWorkspace]);

  const value: WorkspaceContextType = {
    workspaces,
    currentWorkspace,
    isLoadingWorkspaces,
    currentWorkspaceMembers: currentWorkspaceData?.data ?? [],
    isLoadingCurrentWorkspace,
    workspacesError,
    currentWorkspaceError,
    fetchWorkspaces,
    fetchWorkspaceById,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    leaveWorkspace,
    updateWorkspaceInstructions,
    getWorkspaceById,
    refreshWorkspaces,
    refreshCurrentWorkspace,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};
